import { interpret, Machine } from 'xstate'

import {
  LifeInsuranceAnswerOption,
  LifeInsuranceFollowupQuestion,
  LifeInsuranceQuestion,
  LifeInsuranceQuestionType,
} from 'even-ts-static'

import { ApplicationFields, LifeInsuranceQuestionGroups, MappedLifeInsuranceQuestions } from 'embed/constants'
import {
  finalQuestionMachineStates,
  FinalStateNodeNames,
  GeneralizedFollowupQuestion,
  QuestionMachineAction,
  QuestionMachineConfig,
  QuestionMachineEventType,
  QuestionMachineGuard,
  questionMachineId,
  questionMachineOptions,
  QuestionMachineResponsePathWithSteps,
  QuestionMachineState,
  QuestionMachineTransitionDefinition,
} from 'embed/constants/life-insurance'

const emptyMappedLifeInsuranceQuestions: MappedLifeInsuranceQuestions = {}
export const mapLifeInsuranceQuestions = (questions: LifeInsuranceQuestion[]): MappedLifeInsuranceQuestions =>
  questions.reduce(
    (mappedQuestions, question) => ({ ...mappedQuestions, [question.uuid]: question }),
    emptyMappedLifeInsuranceQuestions,
  )

const wildcardValue = '*'

const getPermutations = <T extends unknown>(items: T[]): T[][] => {
  if (items.length === 0) {
    return [[]]
  }

  const [first, ...rest] = items
  const restPermutations = getPermutations(rest)
  return [...restPermutations, ...restPermutations.map((permutation) => [...permutation, first])]
}

const generatePermutedFollowupQuestions = (
  followupQuestions: LifeInsuranceFollowupQuestion[],
  answerOptions: LifeInsuranceAnswerOption[],
): GeneralizedFollowupQuestion[] => {
  const triggeringValueRanks = answerOptions.reduce(
    (final: { [key: string]: number }, answerOption: LifeInsuranceAnswerOption, idx) => ({
      ...final,
      [answerOption.answerValue]: idx,
    }),
    {},
  )

  return getPermutations(followupQuestions)
    .filter((permutation) => permutation.length > 0)
    .map((permutation) =>
      [...permutation].sort(
        (a, b) => triggeringValueRanks[a.triggeringValue] - triggeringValueRanks[b.triggeringValue],
      ),
    )
    .map((permutation) =>
      permutation.reduce(
        (final: GeneralizedFollowupQuestion, followupQuestion: LifeInsuranceFollowupQuestion, idx) => ({
          triggeredQuestionUuid:
            idx === 0
              ? followupQuestion.triggeredQuestionUuid
              : `${final.triggeredQuestionUuid},${followupQuestion.triggeredQuestionUuid}`,
          triggeringValue: Array.isArray(final.triggeringValue)
            ? [...final.triggeringValue, followupQuestion.triggeringValue]
            : [final.triggeringValue, followupQuestion.triggeringValue],
        }),
        { triggeredQuestionUuid: '', triggeringValue: [] },
      ),
    )
}

const generateFollowupQuestionTransitionDefinitions = (
  followupQuestions: GeneralizedFollowupQuestion[],
  nonTriggeringAnswerValues: string[],
): QuestionMachineTransitionDefinition[] => {
  const withoutNonTriggeringValues: QuestionMachineTransitionDefinition[] = followupQuestions.reduce(
    (transitionDefinitions: QuestionMachineTransitionDefinition[], followupQuestion: GeneralizedFollowupQuestion) => {
      const { triggeredQuestionUuid: target, triggeringValue } = followupQuestion
      // If triggeringValue === wildcardValue, then `getQuestionState` will already take care of it
      if (triggeringValue !== wildcardValue) {
        return [
          ...transitionDefinitions,
          {
            target,
            actions: QuestionMachineAction.IncrementFollowupQuestionStep,
            cond: {
              type: QuestionMachineGuard.EventValueIsCondValue,
              value: triggeringValue,
            },
          },
        ]
      }

      return transitionDefinitions
    },
    [],
  )
  const nonTriggeringAnswerValuesPermutations = getPermutations(nonTriggeringAnswerValues).filter(
    (permutation) => permutation.length > 0,
  )

  return withoutNonTriggeringValues.concat(
    nonTriggeringAnswerValuesPermutations.reduce(
      (final: QuestionMachineTransitionDefinition[], permutation): QuestionMachineTransitionDefinition[] => [
        ...final,
        ...withoutNonTriggeringValues.map((transitionDefinition) => ({
          ...transitionDefinition,
          cond: {
            ...transitionDefinition.cond,
            value: Array.isArray(transitionDefinition.cond?.value)
              ? [...(transitionDefinition.cond?.value as string[]), ...permutation]
              : [transitionDefinition.cond?.value as string, ...permutation],
            type: transitionDefinition.cond?.type ?? QuestionMachineGuard.EventValueIsCondValue,
          },
        })),
      ],
      [],
    ),
  )
}

const getNonTriggeringAnswerValues = (question: LifeInsuranceQuestion): string[] =>
  question.answerOptions?.reduce(
    (final: string[], answerOption) =>
      !question.followupQuestions?.find(
        (followupQuestion) => followupQuestion.triggeringValue === answerOption.answerValue,
      )
        ? [...final, answerOption.answerValue]
        : final,
    [],
  ) ?? []

const getHasUnconditionalFollowupQuestion = (question: LifeInsuranceQuestion): boolean =>
  !!(question.followupQuestions?.length === 1) && question.followupQuestions[0].triggeringValue === wildcardValue

const getQuestionState = (
  uuids: string,
  nextQuestionUuid: string,
  questions: MappedLifeInsuranceQuestions,
  states: QuestionMachineState,
): QuestionMachineState => {
  const [firstUuid, ...restUuids] = uuids.split(',')

  const currentQuestion = questions[firstUuid]

  const hasUnconditionalFollowupQuestion = getHasUnconditionalFollowupQuestion(currentQuestion)
  const hasMultipleUuids = restUuids.length > 0
  const unconditionalTransitionDefinition: QuestionMachineTransitionDefinition = {
    target: hasUnconditionalFollowupQuestion
      ? [currentQuestion.followupQuestions?.[0].triggeredQuestionUuid, ...restUuids].join(',')
      : hasMultipleUuids
      ? restUuids.join(',')
      : nextQuestionUuid ?? FinalStateNodeNames.Complete,
    actions:
      hasUnconditionalFollowupQuestion || hasMultipleUuids
        ? QuestionMachineAction.IncrementFollowupQuestionStep
        : QuestionMachineAction.IncrementCurrentStep,
  }

  const followupQuestions = (currentQuestion.questionType === LifeInsuranceQuestionType.SelectMultiple
    ? generatePermutedFollowupQuestions(currentQuestion.followupQuestions ?? [], currentQuestion.answerOptions ?? [])
    : currentQuestion.followupQuestions ?? []
  ).map((followupQuestion) => ({
    ...followupQuestion,
    triggeredQuestionUuid: [followupQuestion.triggeredQuestionUuid, ...restUuids].join(','),
  }))

  const followupQuestionTransitionDefinitions = generateFollowupQuestionTransitionDefinitions(
    followupQuestions,
    currentQuestion.questionType === LifeInsuranceQuestionType.SelectMultiple
      ? getNonTriggeringAnswerValues(currentQuestion)
      : [],
  )

  const currentQuestionState: QuestionMachineState[keyof QuestionMachineState] = {
    on: {
      [QuestionMachineEventType.ANSWER]: [...followupQuestionTransitionDefinitions, unconditionalTransitionDefinition],
    },
  }

  let followupQuestionsUuids = followupQuestions.map((followupQuestion) => followupQuestion.triggeredQuestionUuid)
  if (hasUnconditionalFollowupQuestion) {
    followupQuestionsUuids = [...followupQuestionsUuids, unconditionalTransitionDefinition.target]
  }
  const unprocessedFollowupQuestionUuids = followupQuestionsUuids.filter(
    (followupQuestionUuid) => !states[followupQuestionUuid],
  )
  const followupQuestionsStates = unprocessedFollowupQuestionUuids.reduce(
    (newStates: QuestionMachineState, newUuid) => ({
      ...newStates,
      ...getQuestionState(newUuid, nextQuestionUuid, questions, states),
    }),
    {},
  )

  return { ...states, [uuids]: currentQuestionState, ...followupQuestionsStates }
}

export const getQuestionMachineConfig = (
  questions: MappedLifeInsuranceQuestions,
  groups: LifeInsuranceQuestionGroups,
): QuestionMachineConfig => {
  // I wanted to use `Array.prodotype.flat` here but TypeScript was giving me issue during `npm run build`
  const topLevelQuestionUuids = groups.reduce((final, group) => [...final, ...group], [])

  const config: QuestionMachineConfig = {
    id: questionMachineId,
    initial: topLevelQuestionUuids[0],
    context: {
      currentStep: 0,
      totalNumberOfSteps: topLevelQuestionUuids.length,
      followupStep: 0,
    },
    states: finalQuestionMachineStates,
  }

  const nonCompleteQuestionMachineState = topLevelQuestionUuids.reduce((states: QuestionMachineState, uuid, idx) => {
    const nextQuestionUuid = topLevelQuestionUuids[idx + 1]
    return { ...states, ...getQuestionState(uuid, nextQuestionUuid, questions, states) }
  }, {})

  return {
    ...config,
    states: {
      ...config.states,
      ...nonCompleteQuestionMachineState,
    },
  }
}

export const getQuestionUuidFromQuestionMachineState = (machineStateName: string): string =>
  machineStateName.split(',')[0]

export const getResponsePathWithSteps = (
  machineConfig: QuestionMachineConfig,
  applicationFields?: ApplicationFields,
): QuestionMachineResponsePathWithSteps => {
  const machine = interpret(Machine(machineConfig, questionMachineOptions))
  machine.start()

  let currentState = String(machine.state.value)
  let currentQuestionUuid = getQuestionUuidFromQuestionMachineState(currentState)

  const responsePathWithSteps: QuestionMachineResponsePathWithSteps = {
    responsePath: [],
    stepMap: {},
    isComplete: false,
  }
  while (applicationFields?.[currentQuestionUuid]?.isValid) {
    const currentField = applicationFields[currentQuestionUuid]
    responsePathWithSteps.responsePath.push({ currentState, value: currentField.value })
    const {
      context: { currentStep, followupStep },
    } = machine.state
    responsePathWithSteps.stepMap[currentState] = { currentStep, followupStep }
    machine.send({ type: QuestionMachineEventType.ANSWER, value: currentField.value })
    currentState = String(machine.state.value)
    currentQuestionUuid = getQuestionUuidFromQuestionMachineState(currentState)
    if (currentState === FinalStateNodeNames.Complete) {
      responsePathWithSteps.isComplete = true
    }
  }
  responsePathWithSteps.responsePath.push({ currentState, value: applicationFields?.[currentQuestionUuid]?.value })
  responsePathWithSteps.stepMap[currentState] = {
    currentStep: machine.state.context.currentStep,
    followupStep: machine.state.context.followupStep,
  }

  return responsePathWithSteps
}
